import { useCallback, useState } from 'react'
import { useAccount, useConfig, usePublicClient, useWalletClient, useCapabilities } from 'wagmi'
import { sendCalls, waitForCallsStatus } from '@wagmi/core'
import { encodeFunctionData } from 'viem'
import { useToast } from '@/providers/ToastProvider'
import { ERC20_ABI } from '@/lib/erc20'
import { PERMIT2_ADDRESS, signPermitTransfer } from '@/lib/permit2'

const MAX_UINT = 2n ** 256n - 1n

/// The single transaction chokepoint for the whole app (INTENT-UX-PLAN.md). Every action button
/// routes an *intent* through here - an ordered list of calls plus the token pulls the intent
/// needs - and this hook executes it as ONE confirmation wherever the wallet can, falling back to
/// today's sequential prompts where it cannot. Buttons stop caring how many calls or approvals sit
/// underneath; they describe what the user wants and hand it over.
///
/// intent = {
///   calls:  [{ address, abi, functionName, args, value? }, ...],   // the "what", in order
///   pulls?: [{ token, spender, amount }, ...],                     // ERC20 the intent moves
///   successMsg?, pendingMsg?,
/// }
///
/// Capability ladder (best available first), selected from the wallet's advertised capabilities:
///   1. atomic batch (EIP-5792): approvals for un-approved pulls are prepended INTO the batch, and
///      the whole thing is one `wallet_sendCalls` - a single confirmation.
///   4. no atomic batch: today's exact behaviour - approve (only where allowance is short) then the
///      calls, sequential `writeContract`s, one prompt each. No regression on legacy wallets.
///
/// Permit2 rungs (2/3 in the plan - approvals as signatures) land in Phase 2; the `pulls` shape is
/// already the seam they slot into (a pull resolves to a Permit2 permit instead of an approve).
export function useAction() {
  const config = useConfig()
  const publicClient = usePublicClient()
  const { address: account, chainId, isConnected } = useAccount()
  // Same reconnect-race guard useContractTx documents: resolve the wallet client only once the
  // connector has genuinely finished reconnecting, or getProvider() can throw mid-restore.
  const { data: walletClient } = useWalletClient({ query: { enabled: isConnected } })
  const toast = useToast()
  const atomic = useAtomicBatchSupported()

  const [pending, setPending] = useState(false)
  const [error, setError] = useState(null)

  /// Which pulls actually need an approval right now (allowance below the amount). Reads are cheap
  /// and parallel; a pull already covered by an existing allowance adds nothing to the intent.
  const resolveApprovals = useCallback(async (pulls) => {
    if (!pulls?.length) return []
    const checks = await Promise.all(pulls.map(async (p) => {
      const allowance = await publicClient.readContract({
        address: p.token, abi: ERC20_ABI, functionName: 'allowance', args: [account, p.spender],
      })
      return allowance >= p.amount ? null : p
    }))
    return checks.filter(Boolean)
  }, [publicClient, account])

  const run = useCallback(async (intent, opts = {}) => {
    if (!walletClient || !account) {
      toast.push('Connect a wallet first', 'error')
      return null
    }
    const { calls = [], pulls = [], successMsg, pendingMsg } = { ...intent, ...opts }
    setPending(true)
    setError(null)
    try {
      const needApprove = await resolveApprovals(pulls)

      // ── Rung 1: atomic batch - one confirmation for approvals + every call. ──
      if (atomic.supported) {
        const batch = [
          ...needApprove.map((p) => ({
            to: p.token,
            data: encodeFunctionData({ abi: ERC20_ABI, functionName: 'approve', args: [p.spender, MAX_UINT] }),
          })),
          ...calls.map((c) => ({
            to: c.address,
            data: encodeFunctionData({ abi: c.abi, functionName: c.functionName, args: c.args }),
            value: c.value ?? 0n,
          })),
        ]
        if (pendingMsg) toast.push(pendingMsg, 'info')
        const { id } = await sendCalls(config, { account, chainId, calls: batch })
        const res = await waitForCallsStatus(config, { id })
        if (res.status === 'success') {
          toast.push(successMsg || 'Done', 'success')
          return res
        }
        toast.push('Transaction reverted', 'error')
        return null
      }

      // ── Rung 3: Permit2 - a wallet that can't batch can still collapse approve+action into ONE
      //    on-chain tx by signing a permit. Used only when the flow offers a `permit` descriptor
      //    (its contract has a *WithPermit entrypoint) and the token pull is the whole prerequisite;
      //    buildCall returns the permit-consuming call that replaces the plain approve+call. ──
      if (intent.permit) {
        const { token, amount, spender, buildCall, preCalls = [] } = intent.permit
        // One-time: approve the TOKEN to the Permit2 singleton (not to each spender). After this a
        // signature covers every future action on this token, on any of our contracts.
        const p2allow = await publicClient.readContract({ address: token, abi: ERC20_ABI, functionName: 'allowance', args: [account, PERMIT2_ADDRESS] })
        if (p2allow < amount) {
          await sequential({ address: token, abi: ERC20_ABI, functionName: 'approve', args: [PERMIT2_ADDRESS, MAX_UINT] })
        }
        // Non-permit prerequisites that must land before the permit call (e.g. Multiply's borrow
        // delegation + one-step session-operator grants - pool-state authorisations that can't be a
        // signature). Empty for the simple flows.
        for (const pc of preCalls) await sequential(pc)
        if (pendingMsg) toast.push(pendingMsg, 'info')
        const { permit, signature } = await signPermitTransfer(walletClient, { owner: account, token, amount, spender, chainId })
        const last = await sequential(buildCall(permit, signature))
        toast.push(successMsg || 'Done', 'success')
        return last
      }

      // ── Rung 4: sequential fallback - exactly today's behaviour. ──
      for (const p of needApprove) {
        await sequential({ address: p.token, abi: ERC20_ABI, functionName: 'approve', args: [p.spender, MAX_UINT] })
      }
      if (pendingMsg) toast.push(pendingMsg, 'info')
      let last = null
      for (const c of calls) {
        last = await sequential(c)
      }
      toast.push(successMsg || 'Done', 'success')
      return last
    } catch (e) {
      const msg = e?.shortMessage || e?.message || 'Transaction failed'
      setError(msg)
      toast.push(msg.slice(0, 180), 'error', 6000)
      return null
    } finally {
      setPending(false)
    }

    // Simulate-then-write-then-wait for one call, throwing on revert so the caller's try/catch
    // surfaces the friendly reason. Same shape as useContractTx's `run`, inlined so the fallback
    // ladder controls its own toasting (one final success, not one per leg).
    async function sequential(c) {
      const sim = await publicClient.simulateContract({ account, address: c.address, abi: c.abi, functionName: c.functionName, args: c.args, value: c.value })
      const hash = await walletClient.writeContract(sim.request)
      const receipt = await publicClient.waitForTransactionReceipt({ hash })
      if (receipt.status !== 'success') throw new Error('Transaction reverted')
      return receipt
    }
  }, [walletClient, account, chainId, atomic.supported, config, publicClient, resolveApprovals, toast])

  return { run, pending, error, atomic: atomic.supported }
}

/// Does the connected wallet advertise atomic batching (EIP-5792) on the active chain? Used both
/// by useAction to pick a rung and by action UIs to promise "one confirmation" honestly instead of
/// "a few". Tolerates both the current `atomic.status` shape and the older `atomicBatch.supported`
/// one, and treats an absent/erroring capability as unsupported (so the answer is the safe default).
export function useAtomicBatchSupported() {
  const { chainId } = useAccount()
  const { data } = useCapabilities({ query: { enabled: true } })
  const caps = data?.[chainId]
  const status = caps?.atomic?.status
  const supported = status === 'supported' || status === 'ready' || caps?.atomicBatch?.supported === true
  return { supported: Boolean(supported) }
}
